import { apiClient } from './client';
import type { Verfahren, VerfahrenListItem, VerfahrenDetail, VerfahrenStats, StateInfo, AuditLogEntry } from '../types';
import type { PaginatedResponse } from '../types/api';

export interface VerfahrenQueryParams {
  page?: number;
  per_page?: number;
  staat?: string;
  zustaendig?: string;
  vermieter_id?: number;
  search_text?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  include_hidden?: boolean;
}

export type VerfahrenListResponse = PaginatedResponse<VerfahrenListItem>;

export interface VerfahrenUpdateData {
  vermieter_id?: number | null;
  aktenzeichen?: string;
  zustaendig?: string;
  frist?: string;
  notes?: string;
}

export const verfahrenApi = {
  getVerfahren: async (params: VerfahrenQueryParams = {}): Promise<VerfahrenListResponse> => {
    const { data } = await apiClient.get('/verfahren/', { params });
    return data;
  },

  getVerfahrenDetail: async (id: number): Promise<VerfahrenDetail> => {
    const { data } = await apiClient.get(`/verfahren/${id}`);
    return data;
  },

  createVerfahren: async (body: { listing_id: number; aktenzeichen?: string; notes?: string }): Promise<Verfahren> => {
    const { data } = await apiClient.post('/verfahren/', body);
    return data;
  },

  updateVerfahren: async (id: number, updates: VerfahrenUpdateData): Promise<Verfahren> => {
    const { data } = await apiClient.patch(`/verfahren/${id}`, updates);
    return data;
  },

  changeState: async (
    id: number,
    newState: string,
    comment?: string,
    handoff?: { assignee_user_id: number; assignment_context: string },
  ): Promise<{ verfahren: Verfahren; warning: string | null }> => {
    const { data } = await apiClient.patch(`/verfahren/${id}/staat`, {
      new_state: newState,
      comment,
      ...(handoff ? handoff : {}),
    });
    return data;
  },

  getHistory: async (id: number): Promise<AuditLogEntry[]> => {
    const { data } = await apiClient.get(`/verfahren/${id}/verlauf`);
    return data;
  },

  getStats: async (): Promise<VerfahrenStats> => {
    const { data } = await apiClient.get('/verfahren/stats');
    return data;
  },

  getStates: async (): Promise<{ states: StateInfo[] }> => {
    const { data } = await apiClient.get('/verfahren/states');
    return data;
  },

  merge: async (targetId: number, sourceIds: number[]): Promise<{ verfahren: Verfahren; merged_ids: number[]; inserate_moved: number }> => {
    const { data } = await apiClient.post(`/verfahren/${targetId}/merge`, {
      source_ids: sourceIds,
    });
    return data;
  },

  attachInserat: async (verfahrenId: number, inseratId: number): Promise<void> => {
    await apiClient.post(`/verfahren/${verfahrenId}/inserate/${inseratId}`);
  },

  detachInserat: async (verfahrenId: number, inseratId: number): Promise<void> => {
    await apiClient.delete(`/verfahren/${verfahrenId}/inserate/${inseratId}`);
  },

  linkEinheit: async (verfahrenId: number, einheitId: number | null): Promise<void> => {
    await apiClient.patch(`/verfahren/${verfahrenId}/einheit`, { einheit_id: einheitId });
  },
};
